import React from 'react';
import { Box, Button, Typography } from 'components/index';
import ProjectCompsDialog from '../components/project-comps/ProjectCompsDialog';
import { compareArrays } from '../components/report/report.util';
import InputCCReportDialog from '../components/construction-cost-report/InputCCReportDialog';
import { CCReportInfo, InputCCReportDTO } from '../components/construction-cost-report/construction-cost-report.type';
import { toast } from 'react-toastify';
import {
  bulkDeleteCCReports,
  createCCReport,
  deleteCCReport,
  getCCReports,
} from '../components/construction-cost-report/construction-cost-report.api';
import keys from 'common/react-query/data-keys';
import { Menu, styled, Stack, Badge, Tooltip } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
  MRT_TablePagination,
} from 'material-react-table';
import { GridFilterModel, GridLogicOperator, GridFilterItem, GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from 'react-query';
import { handleError } from '../common/utils/util.errors';
import dataKeys from '../common/react-query/data-keys';
import { ROUTES } from '../common/constants/urls';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDateOperators, getQueryStringForSieve, getStringOperators } from '../common/utils/datagrid-sieve.util';
import { formatISODate, getFullName } from '../common/utils/common.util';
import { Delete as DeleteIcon, ViewColumnOutlined as ViewColumnIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import TableSkeletonLoader from '../components/general/TableSkeletonLoader';
import { useAuth } from 'contexts/AuthContext';
import { getFromLocalStorage, storeInLocalStorage } from 'common/utils/util.cache';
import { useDebounce } from '@uidotdev/usehooks';
import { getAppliedFiltersCount } from '../components/filter-panel/filter-panel.util';
import FilterPanel from '../components/filter-panel/FilterPanel';
import { usePrompt } from '../components/AppPrompt';
import WhiteFab from '../components/common/WhiteFab';
import { CustomTooltip } from '../components/common/CustomTooltip';
import { IconOverTextButton } from '../components/common/IconOverTextButton';
import { GridRowId } from '@mui/x-data-grid';
import { useHasActionRole } from '../components/common/PermissionGate';
import { useMRTStyling } from '../hooks/useMRTStyling';
import { CustomManageColumnsMenu } from '../components/common/CustomManageColumnsMenu';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_MS = 500;
const cacheKey = `cc-reports-table-preferences`;
const FILTERS_KEY = 'cc-reports-table-filters';
const DEFAULT_FILTERS: GridFilterModel = { items: [], logicOperator: GridLogicOperator.And };

const getInitialFilters = (items: GridFilterItem[]) => {
  const savedFilters = getFromLocalStorage(FILTERS_KEY);
  return items
    ? {
        items: items,
        logicOperator: GridLogicOperator.And,
      }
    : DEFAULT_FILTERS;
};

const saveFilters = (filters: GridFilterModel) => {
  storeInLocalStorage(FILTERS_KEY, filters);
};

const StyledDeleteIcon = styled(DeleteIcon)<{ color?: string }>(({ theme, color = 'gray' }) => ({
  color: color,
  '&:hover': {
    color: theme.palette.error.main,
  },
}));

type CCReport = {
  id: string;
  name: string;
  createdOn: string | undefined;
  createdBy: any;
};

const noRowsOverlay = () => (
  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
    <Typography>No Reports Found</Typography>
  </Box>
);

const CCReportsList = () => {
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling, getOnSortingChangeWithToast } = useMRTStyling<CCReport>();
  const {
    filterDialogOpen,
    inputReportDialogOpen,
    reportData,
    isCreatingReport,
    handleStartCreatingReport,
    closeFilterDialog,
    closeInputReportDialog,
    handleProjectsSelection,
    handleSelectComparables,
    handleCreateReport,
  } = useCCReportCreation();
  const [paginationModel, setPaginationModel] = React.useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortingModel, setSortingModel] = React.useState([{ id: 'createdOn', desc: true }]);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(DEFAULT_FILTERS);
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = React.useState(
    getQueryStringForSieve({ page: 0, pageSize: DEFAULT_PAGE_SIZE }, [{ field: 'createdOn', sort: 'desc' }], {
      items: [],
      logicOperator: GridLogicOperator.And,
    }),
  );
  const debouncedQueryParams = useDebounce(queryParams, DEBOUNCE_MS);
  const queryClient = useQueryClient();
  const [initialDataLoaded, setInitialDataLoaded] = React.useState(true);
  const [selectedReport, setSelectedReport] = React.useState<any | null>(null);
  const { getConfirmation } = usePrompt();
  const [selectedReports, setSelectedReports] = React.useState<string[]>([]);
  const rowCountRef = React.useRef(0);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = React.useState(false);
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>({});

  React.useEffect(() => {
    if (searchParams.get('showOnlyMyReports') == 'true') {
      toggleMyReportsFilter();
    }
  }, [searchParams]);

  const handleDelete = async (report: CCReportInfo) => {
    const isConfirmed = await getConfirmation({
      heading: 'Delete Report',
      description: `Are you sure you want to delete this report: ${report.name}?`,
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (!isConfirmed) return;
    try {
      setIsDeleting(true);
      await deleteCCReport(report.id);
      toast.success('Report deleted successfully');
      queryClient.invalidateQueries(keys.ccReports);
    } catch (error) {
      handleError(error);
    }
    setIsDeleting(false);
  };

  const handleBulkDelete = async () => {
    const reportIds = selectedReports;
    if (reportIds.length === 0) {
      toast.error('Please select at least one report!');
      return;
    }
    const isConfirmed = await getConfirmation({
      heading: 'Delete Reports',
      description: 'Are you sure you want to delete these report(s)?',
      confirmButton: {
        label: 'Delete',
        color: 'error',
      },
      cancelButton: {
        label: 'Cancel',
        color: 'secondary',
      },
    });

    if (isConfirmed) {
      try {
        setIsBulkDeleting(true);
        await bulkDeleteCCReports({ reportIds });
        toast.success('Reports(s) deleted successfully');
        queryClient.invalidateQueries([dataKeys.ccReports, debouncedQueryParams]);
        setSelectedReports([]);
      } catch (error) {
        handleError(error);
      }
      setIsBulkDeleting(false);
    }
  };

  const canDelete = useHasActionRole('DELETE_CC_REPORT');

  // Define your columns for the MaterialReactTable
  const columns = React.useMemo<MRT_ColumnDef<CCReport>[]>(() => {
    const base: MRT_ColumnDef<CCReport>[] = [
      {
        accessorKey: 'name',
        header: 'Report Name',
        size: 300,
        Cell: ({ row }: { row: MRT_Row<CCReport> }) => (
          <CustomTooltip title={row.original.name ? String(row.original.name) : ''}>
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                maxWidth: '100%',
              }}
            >
              {row.original.name ? String(row.original.name) : '--'}
            </span>
          </CustomTooltip>
        ),
      },
      {
        accessorKey: 'createdOn',
        header: 'Generated On',
        size: 150,
        Cell: ({ row }: { row: MRT_Row<CCReport> }) => {
          if (!row.original.createdOn) return '--';
          return formatISODate(row.original.createdOn);
        },
      },
      {
        accessorKey: 'createdBy',
        header: 'Generated By',
        size: 150,
        Cell: ({ row }: { row: MRT_Row<CCReport> }) => {
          const createdBy = row.original.createdBy;
          return createdBy ? getFullName(createdBy) : '--';
        },
      },
    ];

    const actionColumn: MRT_ColumnDef<CCReport> = {
      id: 'actions',
      header: '',
      size: 60,
      minSize: 60,
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableHiding: false,
      ...getActionsColumnStyling(),
      Header: ({ table }: { table: any }) => (
        <Stack sx={{ width: '100%' }} direction='row' spacing={1} alignItems='center' justifyContent='flex-end'>
          <Tooltip title='Delete Selected Reports' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                onClick={handleBulkDelete}
                disabled={selectedReports.length === 0}
                loading={isBulkDeleting}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
      Cell: ({ row }: { row: MRT_Row<CCReport> }) => (
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='center' sx={{ width: '100%' }}>
          <Tooltip title='Delete Report' arrow={false}>
            <span>
              <WhiteFab
                size='small'
                icon={<DeleteIcon fontSize='small' />}
                textColor='error'
                disabled={selectedReports.length > 0 || isBulkDeleting || isDeleting}
                loading={isDeleting && deletingRowId === row.original.id}
                onClick={async (e: React.MouseEvent) => {
                  e.stopPropagation();
                  setDeletingRowId(row.original.id);
                  await handleDelete(row.original as CCReportInfo);
                  setDeletingRowId(null);
                }}
              />
            </span>
          </Tooltip>
        </Stack>
      ),
    };

    return canDelete ? [...base, actionColumn] : base;
  }, [selectedReports.length, isBulkDeleting, isDeleting, deletingRowId, canDelete]);

  // Create compatible GridColDef for FilterPanel
  const filterColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Report Name',
      type: 'string',
      filterOperators: getStringOperators(),
    },
    {
      field: 'createdOn',
      headerName: 'Generated On',
      type: 'date',
      filterOperators: getDateOperators(),
    },
    {
      field: 'createdBy',
      headerName: 'Generated By',
      type: 'string',
      filterOperators: getStringOperators(),
    },
  ];

  const skippedFields: string[] = [];
  const filteredColumns = filterColumns.filter(column => !skippedFields.includes(column.field));

  const toggleMyReportsFilter = () => {
    const myReportsFilter = {
      field: 'createdBy.Id',
      operator: 'equals',
      value: user?.id ?? '',
    };
    const filterItems = filterModel.items.filter((item: GridFilterItem) => item.field !== myReportsFilter.field);
    if (myReportsFilterOn) {
      setFilterModel({ ...filterModel, items: filterItems });
    } else {
      setFilterModel({ ...filterModel, items: [...filterItems, myReportsFilter] });
    }
  };

  const myReportsFilterOn = React.useMemo(() => {
    return filterModel.items.some((item: GridFilterItem) => item.field === 'createdBy.Id');
  }, [filterModel.items]);

  const showManageColumns = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColumnsMenuAnchorEl(e.currentTarget);
  };

  const { data, isLoading, isError, error } = useQuery(
    [dataKeys.ccReports, debouncedQueryParams],
    () => getCCReports(debouncedQueryParams),
    {
      onSettled: () => {
        setInitialDataLoaded(false);
      },
    },
  );

  React.useEffect(() => {
    setQueryParams(
      getQueryStringForSieve(
        { page: paginationModel.pageIndex, pageSize: paginationModel.pageSize },
        sortingModel.map(sort => ({ field: sort.id, sort: sort.desc ? 'desc' : 'asc' })),
        filterModel,
      ),
    );
  }, [paginationModel, sortingModel, filterModel]);

  if (isError) handleError(error);
  const reports: any[] = data?.reportsList ?? [];

  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});

  React.useEffect(() => {
    const selectedIds = Object.keys(rowSelection);
    setSelectedReports(selectedIds);
  }, [rowSelection]);

  const getPreferences = () => {
    return getFromLocalStorage(cacheKey);
  };

  const savePreferences = () => {
    const state = table.getState();
    storeInLocalStorage(cacheKey, state);
  };

  const resetTable = () => {
    setFilterModel(DEFAULT_FILTERS);
    setPaginationModel({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    setSortingModel([{ id: 'createdOn', desc: true }]);
    setColumnVisibility({});
  };

  const actions = [{ label: 'Reset Table', onClick: resetTable }];

  const handleRowClick = (row: MRT_Row<CCReport>) => {
    const reportId = row.original.id;
    navigate(`${ROUTES.CC_REPORTS}/${reportId}`);
  };

  const rowCount = React.useMemo(() => {
    if (data && data.total !== undefined) {
      rowCountRef.current = data.total;
    }
    return rowCountRef.current;
  }, [data?.total]);

  const table = useMaterialReactTable<CCReport>({
    ...commonMRTStyling,
    columns,
    data: reports,
    enableMultiRowSelection: canDelete,
    enableRowSelection: canDelete,
    columnResizeMode: 'onChange',
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
      showFirstButton: true,
      showLastButton: true,
    },

    getRowId: (row: CCReport) => row.id,
    onPaginationChange: setPaginationModel,
    onSortingChange: getOnSortingChangeWithToast(sortingModel as any, setSortingModel as any),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      pagination: paginationModel,
      sorting: sortingModel,
      columnVisibility,
      density: 'compact',
      isLoading: isLoading,
      rowSelection,
    },
    rowCount: rowCount,
    pageCount: Math.ceil(rowCount / paginationModel.pageSize),
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<CCReport> }) => {
      // Determine row background color
      let backgroundColor = '';

      // Use zebra striping for alternating rows
      backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';

      return {
        onClick: () => handleRowClick(row),
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: (theme: any) => theme.palette.action.hover,
          },
          // Style pinned columns to match row background
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      };
    },
    muiTableBodyCellProps: ({ row }: { row: MRT_Row<CCReport> }) => ({
      sx: {
        border: 'none',
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),

    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        borderRadius: 'inherit',
        border: '1px solid rgba(224, 224, 224, 1)',
      },
    },
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['mrt-row-select'],
        right: ['actions'],
      },
    },
    renderBottomToolbar: ({ table }) => (
      <Stack p={1} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Button onClick={handleStartCreatingReport} disabled={isDeleting || isBulkDeleting}>
          Create New
        </Button>
        <MRT_TablePagination table={table} />
      </Stack>
    ),
  });

  if (isLoading && initialDataLoaded) return <TableSkeletonLoader />;

  return (
    <Box mt={2}>
      <Box sx={{ height: '83vh' }} my={2}>
        <Stack direction={'row'} alignItems='center' justifyContent={'flex-end'} p={1} spacing={2}>
          <Stack direction={'row'} alignItems={'center'} spacing={0}>
            <IconOverTextButton
              icon={<ViewColumnIcon />}
              text='Columns'
              tooltip='Manage Columns'
              onClick={showManageColumns}
              disabled={isDeleting || isBulkDeleting}
              showHoverEffect={false}
              wrapInSpan={true}
            />

            <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
              <IconOverTextButton
                icon={<FilterListIcon />}
                text='Filter'
                tooltip='Filter'
                onClick={(e: any) => setFilterAnchorEl(e.currentTarget)}
                disabled={isDeleting || isBulkDeleting}
                showHoverEffect={false}
              />
            </Badge>
          </Stack>
        </Stack>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => {
            setFilterAnchorEl(null);
          }}
        >
          <FilterPanel columns={filteredColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
        </Menu>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme: any) => theme.palette.background.paper,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <MaterialReactTable table={table} />
        </Box>
      </Box>
      <ProjectCompsDialog
        open={filterDialogOpen}
        onClose={closeFilterDialog}
        isFromReport={true}
        disabledCompsIds={[]}
        onSelect={handleSelectComparables}
        defaultOnDisplayFilters={[
          'City',
          'County',
          'State',
          'CustomArea',
          'PostalCode',
          'ProjectType',
          'UnitCount',
          'GrossBuildingArea',
          'NetArea',
        ]}
      />
      <InputCCReportDialog
        open={inputReportDialogOpen}
        onClose={closeInputReportDialog}
        initialValues={reportData}
        setValues={handleCreateReport}
        loading={isCreatingReport}
      />
      {/* Custom Manage Columns Menu */}
      <CustomManageColumnsMenu anchorEl={columnsMenuAnchorEl} setAnchorEl={setColumnsMenuAnchorEl} table={table} title='Manage Columns' />
    </Box>
  );
};

const useCCReportCreation = () => {
  const [filterDialogOpen, setFilterDialogOpen] = React.useState(false);
  const [inputReportDialogOpen, setInputReportDialogOpen] = React.useState(false);
  const [selectedProjects, setSelectedProjects] = React.useState<readonly GridRowId[]>([]);
  const [reportData, setReportData] = React.useState<InputCCReportDTO>();
  const [isCreatingReport, setIsCreatingReport] = React.useState(false);
  const navigate = useNavigate();

  const openFilterDialog = () => setFilterDialogOpen(true);
  const closeFilterDialog = () => setFilterDialogOpen(false);
  const openInputReportDialog = () => setInputReportDialogOpen(true);
  const closeInputReportDialog = () => setInputReportDialogOpen(false);

  const handleStartCreatingReport = () => {
    setSelectedProjects([]);
    setReportData(undefined);
    openFilterDialog();
  };

  const handleProjectsSelection = React.useCallback(
    (projects: readonly GridRowId[], filterString?: string) => {
      if (!compareArrays(projects, selectedProjects)) {
        setSelectedProjects(projects);
      }
      if (!filterString) {
        filterString = JSON.stringify('');
      }
      setReportData(prevValues => {
        return { ...prevValues, filtersString: filterString };
      });
    },
    [selectedProjects],
  );
  const handleSelectComparables = React.useCallback(
    (projects: readonly GridRowId[], filterString?: string) => {
      if (projects.length === 0) {
        toast.error('Please select at least one project!');
        return;
      }
      handleProjectsSelection(projects, filterString);
      closeFilterDialog();
      openInputReportDialog();
    },
    [selectedProjects],
  );

  const handleCreateReport = async (values: InputCCReportDTO) => {
    setReportData(prevValues => {
      return { ...prevValues, ...values };
    });
    setIsCreatingReport(true);
    try {
      const response = await createCCReport({
        ...reportData,
        ...values,
        comparableProjectsIds: selectedProjects,
      });
      toast.success('Report created successfully!');
      navigate(`${ROUTES.CC_REPORTS}/${response.id}`);
      closeInputReportDialog();
    } catch (error) {
      handleError(error);
    } finally {
      setIsCreatingReport(false);
    }
  };

  return {
    filterDialogOpen,
    inputReportDialogOpen,
    selectedProjects,
    reportData,
    isCreatingReport,
    handleStartCreatingReport,
    closeFilterDialog,
    openInputReportDialog,
    closeInputReportDialog,
    handleProjectsSelection,
    handleSelectComparables,
    handleCreateReport,
  };
};

export default CCReportsList;
